import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueSchemeDetailShellPageMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      pages: {
        schemeDetail: {
          title: "方案详情",
          tabs: {
            basicInfo: "基础信息",
            versions: "版本记录",
            topology: "拓扑绑定",
            campsSeats: "阵营席位",
            stagesTasks: "阶段任务",
            reports: "报告模板",
            intelligence: "智能推演",
          },
          sections: {
            basicInfo: "方案基础信息",
            topology: "拓扑绑定",
            campsSeats: "阵营与席位",
            stagesTasks: "阶段与任务",
            reportTemplates: "报告模板",
            versions: "方案版本",
          },
          actions: {
            bindTopology: "绑定拓扑版本",
            editStagesTasks: "编辑阶段与任务",
            saveStages: "保存阶段",
            dragSort: "拖拽排序",
            refresh: "刷新",
          },
          select: {
            versionPlaceholder: "暂无方案版本",
          },
          notices: {
            schemeReadonly: "当前方案已经不是草稿，只能查看基础信息，不能再修改方案本体。",
            versionReadonly: "当前查看的是已发布版本，设计内容为只读。",
            versionReadonlyDescription:
              "发布版本用于演练创建和执行态复制；如需继续设计，请先基于当前方案创建新的草稿版本。",
            versionEditable: "当前查看的是草稿版本，可以直接编辑设计内容。",
            versionEditableDescription:
              "阵营席位、阶段任务（含任务评分配置）和报告模板都会直接写入当前草稿版本；拓扑版本属于方案级绑定。",
            topologyLocked: "方案已锁定拓扑版本；如需更换拓扑，请复制或新建方案。",
            topologyUnboundDescription:
              "当前方案尚未绑定拓扑版本。绑定后该方案将锁定到该拓扑版本；如需更换拓扑，请复制或新建方案。",
            topologyBindInBasicInfo:
              "拓扑版本在方案基础信息中绑定；工作台仅展示已锁定拓扑的只读预览。",
            versionPublished: "已发布版本",
            versionDraft: "草稿版本",
            noVersionYet: "当前方案还没有任何版本。",
            noVersionYetDescription:
              "先在“版本记录”里创建草稿版本，再继续设计拓扑、阵营席位、阶段任务和报告模板。",
            noPublishedVersion: "当前方案尚未发布版本。",
            emptyStructuredData: "当前为空，未写入结构化数据。",
          },
          publishConfirm: "确认发布当前方案版本？发布后可用于创建演练。",
        },
      },
    },
  },
  en: {
    redblue: {
      pages: {
        schemeDetail: {
          title: "Scheme Detail",
          tabs: {
            basicInfo: "Basic Info",
            versions: "Versions",
            topology: "Topology Binding",
            campsSeats: "Camps and Seats",
            stagesTasks: "Stages and Tasks",
            reports: "Report Templates",
            intelligence: "Intelligent Exercise",
          },
          sections: {
            basicInfo: "Scheme Basics",
            topology: "Topology Binding",
            campsSeats: "Camps and Seats",
            stagesTasks: "Stages and Tasks",
            reportTemplates: "Report Templates",
            versions: "Scheme Versions",
          },
          actions: {
            bindTopology: "Bind Topology Version",
            editStagesTasks: "Edit Stages and Tasks",
            saveStages: "Save Stages",
            dragSort: "Drag to sort",
            refresh: "Refresh",
          },
          select: {
            versionPlaceholder: "No scheme versions yet",
          },
          notices: {
            schemeReadonly: "This scheme is no longer a draft, so the scheme itself is read-only.",
            versionReadonly: "You are viewing a published version, so design data is read-only.",
            versionReadonlyDescription:
              "Published versions are used for activity creation and execution snapshots. Create a new draft version before changing design data.",
            versionEditable:
              "You are viewing a draft version and can edit the design data directly.",
            versionEditableDescription:
              "Camps and seats, stages and tasks including task scoring config, and report templates write straight into the current draft version; the topology version is bound at scheme level.",
            topologyLocked:
              "This scheme has locked its topology version. Copy or create a new scheme to use a different topology.",
            topologyUnboundDescription:
              "This scheme has not bound a topology version yet. Once bound, the scheme is locked to that topology version. Copy or create a new scheme to use another topology.",
            topologyBindInBasicInfo:
              "Topology versions are bound in Scheme Basics. The workbench only shows the locked topology as a read-only preview.",
            versionPublished: "Published Version",
            versionDraft: "Draft Version",
            noVersionYet: "This scheme does not have any versions yet.",
            noVersionYetDescription:
              "Create a draft version in Versions first, then continue with topology, camps and seats, stages and tasks, and report templates.",
            noPublishedVersion: "This scheme has not published any version yet.",
            emptyStructuredData: "No structured data has been written yet.",
          },
          publishConfirm:
            "Publish the current scheme version? Published versions can be used to create activities.",
        },
      },
    },
  },
  ja: {
    redblue: {
      pages: {
        schemeDetail: {
          title: "プラン詳細",
          tabs: {
            basicInfo: "基本情報",
            versions: "版履歴",
            topology: "トポロジー紐付け",
            campsSeats: "陣営と席位",
            stagesTasks: "段階とタスク",
            reports: "レポートテンプレート",
            intelligence: "インテリジェント演習",
          },
          sections: {
            basicInfo: "プラン基本情報",
            topology: "トポロジー紐付け",
            campsSeats: "陣営と席位",
            stagesTasks: "段階とタスク",
            reportTemplates: "レポートテンプレート",
            versions: "プラン版",
          },
          actions: {
            bindTopology: "トポロジー版を紐付け",
            editStagesTasks: "段階とタスクを編集",
            saveStages: "段階を保存",
            dragSort: "ドラッグして並べ替え",
            refresh: "更新",
          },
          select: {
            versionPlaceholder: "プラン版はまだありません",
          },
          notices: {
            schemeReadonly: "このプランはすでに下書きではないため、プラン本体は読み取り専用です。",
            versionReadonly: "現在表示中の版は公開済みのため、設計データは読み取り専用です。",
            versionReadonlyDescription:
              "公開版は演習作成と実行スナップショットに使われます。設計を続ける場合は新しい下書き版を作成してください。",
            versionEditable: "現在表示中の版は下書きなので、設計データを直接編集できます。",
            versionEditableDescription:
              "陣営と席位、段階とタスク（タスク採点設定を含む）、レポートテンプレートは現在の下書き版へ直接書き込みます。トポロジー版はプラン単位で紐付けます。",
            topologyLocked:
              "このプランのトポロジー版はロック済みです。別のトポロジーを使う場合はプランをコピーまたは新規作成してください。",
            topologyUnboundDescription:
              "このプランにはまだトポロジー版が紐付いていません。紐付け後、このプランはそのトポロジー版にロックされます。別のトポロジーを使う場合はプランをコピーまたは新規作成してください。",
            topologyBindInBasicInfo:
              "トポロジー版はプラン基本情報で紐付けます。ワークベンチではロック済みトポロジーを読み取り専用で表示します。",
            versionPublished: "公開版",
            versionDraft: "下書き版",
            noVersionYet: "このプランにはまだ版がありません。",
            noVersionYetDescription:
              "まず「版履歴」で下書き版を作成してから、トポロジー、陣営と席位、段階とタスク、レポートテンプレートを続けてください。",
            noPublishedVersion: "このプランはまだ公開版がありません。",
            emptyStructuredData: "構造化データはまだ書き込まれていません。",
          },
          publishConfirm: "現在のプラン版を公開しますか？公開後は演習作成に利用できます。",
        },
      },
    },
  },
};
